/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import EntriesReviewViewStyles from './EntriesReviewViewStyles';
import { Box, CircularProgress, Grid, Typography, GridLegacy } from '@mui/material';
import CourseHeaderView from '../CourseHeader/CourseHeaderView';
import { FormattedMessage } from 'react-intl';
import EntryBoxView from './EntryBox/EntryBoxView';
import { Entry } from '../../../../types/course/Entry';
import { useViewModel } from '../../../../hooks/useViewModel';
import { EntriesReviewViewModel } from './EntriesReviewViewModel';
import { EntryDetailView } from './EntryDetail/EntryDetailView';
import { Pagination } from '@mui/material';

const EntriesReviewView = observer((): ReactElement => {
	const { classes } = EntriesReviewViewStyles();
	const viewModel: EntriesReviewViewModel = useViewModel(EntriesReviewViewModel);

	return (
		<>
			<Box className={classes.contentContainer}>
				<CourseHeaderView title={'entries.new_entries'} />
				{viewModel.isLoading() ? (
					<Box className={classes.loadingContainer}>
						<CircularProgress className={classes.loading} />
					</Box>
				) : (
					<>
						{viewModel.getNewEntries().length === 0 && (
							<Typography variant={'body1'}>
								<FormattedMessage id={'entries.nothing_to_review'} />
							</Typography>
						)}
						<Box>
							{viewModel.isNewEntriesLoading() ? (
								<Box className={classes.loadingContainer}>
									<CircularProgress className={classes.loading} />
								</Box>
							) : (
								<GridLegacy container spacing={3}>
									{viewModel.getNewEntries().map((entry: Entry) => (
										<EntryBoxView viewModel={viewModel} key={entry.id} entry={entry} />
									))}
								</GridLegacy>
							)}
							{viewModel.getNumberOfPagesNewEntries() > 1 && (
								<Box className={classes.paginationContainer}>
									<Pagination
										page={viewModel.getPageNumberNewEntries()}
										onChange={(e, value: number) => viewModel.setPageNumberNewEntries(value)}
										count={viewModel.getNumberOfPagesNewEntries()}
									/>
								</Box>
							)}
						</Box>
						{viewModel.getApprovedEntries().length > 0 && (
							<Box className={classes.approvedRejectedEntries}>
								<Typography variant={'h1'} className={classes.headline}>
									<FormattedMessage id={'entries.approved'} />
								</Typography>
								{viewModel.isApprovedLoading() ? (
									<Box className={classes.loadingContainer}>
										<CircularProgress className={classes.loading} />
									</Box>
								) : (
									<GridLegacy container spacing={3}>
										{viewModel.getApprovedEntries().map((entry: Entry) => (
											<EntryBoxView viewModel={viewModel} key={entry.id} entry={entry} />
										))}
									</GridLegacy>
								)}
								{viewModel.getNumberOfPagesApproved() > 1 && (
									<Box className={classes.paginationContainer}>
										<Pagination
											page={viewModel.getPageNumberApproved()}
											onChange={(e, value: number) => viewModel.setPageNumberApproved(value)}
											count={viewModel.getNumberOfPagesApproved()}
										/>
									</Box>
								)}
							</Box>
						)}
						{viewModel.getRejectedEntries().length > 0 && (
							<Box className={classes.approvedRejectedEntries}>
								<Typography variant={'h1'} className={classes.headline}>
									<FormattedMessage id={'entries.rejected'} />
								</Typography>
								{viewModel.isRejectedLoading() ? (
									<Box className={classes.loadingContainer}>
										<CircularProgress className={classes.loading} />
									</Box>
								) : (
									<GridLegacy container spacing={3}>
										{viewModel.getRejectedEntries().map((entry: Entry) => (
											<EntryBoxView viewModel={viewModel} key={entry.id} entry={entry} />
										))}
									</GridLegacy>
								)}
								{viewModel.getNumberOfPagesRejected() > 1 && (
									<Box className={classes.paginationContainer}>
										<Pagination
											page={viewModel.getPageNumberRejected()}
											onChange={(e, value: number) => viewModel.setPageNumberRejected(value)}
											count={viewModel.getNumberOfPagesRejected()}
										/>
									</Box>
								)}
							</Box>
						)}
					</>
				)}
			</Box>
			{viewModel.isDetailView() && !!viewModel.getEntry() && (
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				<EntryDetailView open={viewModel.isDetailView()} entry={viewModel.getEntry()!} />
			)}
		</>
	);
});

export default EntriesReviewView;
